"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import Image from "next/image";
import Link from "next/link";
import { Check } from "lucide-react";
import { cn } from "@/utilities/core/cn";
import { matchCardBackgroundStyle } from "@/utilities/styles/matchCardBackground";
import type { BracketMatch, BracketParticipant } from "./types";
interface BracketMatchCardProps {
  match: BracketMatch;
  isFirst: boolean;
}
export function TeamRow(t0) {
  const $ = _c(21);
  const {
    participant,
    isFinished
  } = t0;
  let t1;
  if ($[0] !== participant.imageUrl || $[1] !== participant.isTbd || $[2] !== participant.name) {
    t1 = <div className="w-6 h-6 shrink-0">{participant.imageUrl && !participant.isTbd ? <Image src={participant.imageUrl} alt={participant.name} width={24} height={24} className="object-contain" /> : <div className="w-6 h-6 rounded-full bg-muted" />}</div>;
    $[0] = participant.imageUrl;
    $[1] = participant.isTbd;
    $[2] = participant.name;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = participant.isTbd && "italic text-muted-foreground";
  const t3 = participant.isWinner && "font-semibold";
  const t4 = !participant.isWinner && isFinished && !participant.isTbd && "text-muted-foreground";
  let t5;
  if ($[4] !== t2 || $[5] !== t3 || $[6] !== t4) {
    t5 = cn("flex-1 text-xs truncate", t2, t3, t4);
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = participant.isTbd ? "TBD" : participant.name;
  let t7;
  if ($[8] !== t5 || $[9] !== t6) {
    t7 = <span className={t5}>{t6}</span>;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== participant.isQualified) {
    t8 = participant.isQualified && <Check className="w-3.5 h-3.5 shrink-0 text-emerald-500" />;
    $[11] = participant.isQualified;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== participant.isWinner || $[14] !== participant.score) {
    t9 = participant.score !== null && <span className={cn("text-xs tabular-nums font-mono min-w-6 text-center rounded-md px-1 py-0.5", participant.isWinner ? "font-bold bg-primary/10 text-primary" : "text-muted-foreground")}>{participant.score}</span>;
    $[13] = participant.isWinner;
    $[14] = participant.score;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t1 || $[17] !== t7 || $[18] !== t8 || $[19] !== t9) {
    t10 = <div className="flex items-center gap-2 py-0.5">{t1}{t7}{t8}{t9}</div>;
    $[16] = t1;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  return t10;
}
export function BracketMatchCard(t0) {
  const $ = _c(36);
  const {
    match,
    isFirst
  } = t0;
  const isLive = match.status === "live";
  const isFinished = match.status === "finished";
  const hasWinner = isFinished && (match.home.isWinner || match.away.isWinner);
  const t1 = isLive && "border-primary/50 ring-1 ring-primary/20";
  const t2 = !isFirst && "mt-1.5";
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = cn("relative match-card-surface rounded-(--radius) border border-border/50 transition-all", "shadow-(--shadow-sm) hover:shadow-(--shadow)", t1, t2);
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== hasWinner) {
    t4 = hasWinner && <div className="absolute left-0 top-2 bottom-2 w-0.75 rounded-full bg-primary" />;
    $[3] = hasWinner;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = hasWinner && "pl-3.5";
  let t6;
  if ($[5] !== t5) {
    t6 = cn("p-2.5", t5);
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== match.status || $[8] !== match.statusText) {
    t7 = match.status === "scheduled" && match.statusText && <div className="text-[10px] text-muted-foreground mb-1.5">{match.statusText}</div>;
    $[7] = match.status;
    $[8] = match.statusText;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== isLive || $[11] !== match.statusText) {
    t8 = isLive && <div className="flex items-center gap-1.5 mb-1.5"><span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-primary" /></span><span className="text-[10px] font-bold uppercase text-primary">{match.statusText || "LIVE"}</span></div>;
    $[10] = isLive;
    $[11] = match.statusText;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== isFinished || $[14] !== match.home) {
    t9 = <TeamRow participant={match.home} isFinished={isFinished} />;
    $[13] = isFinished;
    $[14] = match.home;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="h-px my-1" />;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== isFinished || $[18] !== match.away) {
    t11 = <TeamRow participant={match.away} isFinished={isFinished} />;
    $[17] = isFinished;
    $[18] = match.away;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== match.statusNote) {
    t12 = match.statusNote && <div className="mt-1.5 text-[10px] text-muted-foreground text-center">{match.statusNote}</div>;
    $[20] = match.statusNote;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t11 || $[23] !== t12 || $[24] !== t6 || $[25] !== t7 || $[26] !== t8 || $[27] !== t9) {
    t13 = <div className={t6}>{t7}{t8}{t9}{t10}{t11}{t12}</div>;
    $[22] = t11;
    $[23] = t12;
    $[24] = t6;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t13 || $[30] !== t3 || $[31] !== t4) {
    t14 = <div className={t3} style={matchCardBackgroundStyle}>{t4}{t13}</div>;
    $[29] = t13;
    $[30] = t3;
    $[31] = t4;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  const card = t14;
  if (match.url) {
    let t15;
    if ($[33] !== card || $[34] !== match.url) {
      t15 = <Link href={match.url} prefetch={false} className="block">{card}</Link>;
      $[33] = card;
      $[34] = match.url;
      $[35] = t15;
    } else {
      t15 = $[35];
    }
    return t15;
  }
  return card;
}